import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AutoAwesome as AutoAwesomeIcon,
  Mic as MicIcon,
  TextSnippet as TextSnippetIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import koLocale from 'date-fns/locale/ko';
import { PageHeader, LoadingSpinner } from '../../components/common';
import { useAuthStore } from '../../hooks/useAuth';
import { useConsultationListViewModel } from '../../viewmodels';
import { consultationService, recordingService } from '../../services/consultationService';
import { templateService } from '../../services/documentService';
import { promptService } from '../../services/promptService';
import { Recording, Prompt, Template } from '../../models';

const ConsultationListPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const hasWedmsToken = user?.has_wedms_token;
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [search, setSearch] = useState('');

  // AI 생성 다이얼로그 상태
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // 녹음 파일 목록 (AI 분석 가능한 것만)
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [selectedRecordingId, setSelectedRecordingId] = useState<number | ''>('');
  const [loadingRecordings, setLoadingRecordings] = useState(false);

  // 템플릿 및 프롬프트
  const [templates, setTemplates] = useState<Template[]>([]);
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | ''>('');
  const [selectedPromptId, setSelectedPromptId] = useState<number | ''>('');
  const [llmRole, setLlmRole] = useState('요양보호 상담 전문가');

  // STT 텍스트 미리보기
  const [sttPreviewText, setSttPreviewText] = useState<string>('');
  const [loadingSttPreview, setLoadingSttPreview] = useState(false);

  const {
    consultations,
    totalCount,
    isLoading,
    deleteConsultation,
    refetch,
  } = useConsultationListViewModel({
    page: page + 1,
    page_size: rowsPerPage,
    search: search || undefined,
  });

  // LLM 처리 중인 상담이 있으면 자동 폴링
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // LLM 처리 중인 상담 확인
    const processingConsultations = consultations.filter(
      (c) => c.llm_status === 'processing'
    );

    // 처리 중인 상담이 없으면 폴링 중지
    if (processingConsultations.length === 0) {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      return;
    }

    // 이미 폴링 중이면 스킵
    if (pollingIntervalRef.current) return;

    // 5초 간격으로 목록 새로고침
    pollingIntervalRef.current = setInterval(() => {
      refetch();
    }, 5000);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [consultations, refetch]);

  // 템플릿 및 프롬프트 로드
  useEffect(() => {
    const loadData = async () => {
      try {
        const [templateList, promptList] = await Promise.all([
          templateService.getList(),
          promptService.getList(),
        ]);
        setTemplates(templateList);
        setPrompts(promptList);

        // 기본값 설정
        if (templateList.length > 0) {
          setSelectedTemplateId(templateList[0].id);
        }
        const defaultPrompt = promptList.find(p => p.is_default);
        if (defaultPrompt) {
          setSelectedPromptId(defaultPrompt.id);
        } else if (promptList.length > 0) {
          setSelectedPromptId(promptList[0].id);
        }
      } catch (err) {
        console.error('Failed to load templates/prompts:', err);
      }
    };
    loadData();
  }, []);

  // 녹음 파일 선택 시 STT 텍스트 로드
  useEffect(() => {
    if (!selectedRecordingId) {
      setSttPreviewText('');
      return;
    }

    const loadSttText = async () => {
      setLoadingSttPreview(true);
      try {
        const result = await recordingService.getSTTStatus(selectedRecordingId as number);
        setSttPreviewText(result.stt_text || '');
      } catch (err) {
        console.error('Failed to load STT text:', err);
        setSttPreviewText('');
      } finally {
        setLoadingSttPreview(false);
      }
    };
    loadSttText();
  }, [selectedRecordingId]);

  // 다이얼로그 열기 시 녹음 파일 로드
  const handleOpenCreateDialog = async () => {
    setCreateDialogOpen(true);
    setCreateError(null);
    setSelectedRecordingId('');
    setSttPreviewText('');
    setLoadingRecordings(true);

    try {
      const availableRecordings = await recordingService.getAvailableForAI();
      setRecordings(availableRecordings);
    } catch (err) {
      console.error('Failed to load recordings:', err);
      setCreateError('녹음 파일 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoadingRecordings(false);
    }
  };

  // AI 상담 기록 생성
  const handleCreateWithAI = async () => {
    if (!selectedRecordingId || !selectedTemplateId || !selectedPromptId) {
      setCreateError('녹음 파일, 템플릿, 프롬프트를 모두 선택해주세요.');
      return;
    }

    setIsCreating(true);
    setCreateError(null);

    try {
      const selectedRecording = recordings.find(r => r.id === selectedRecordingId);
      const filename = selectedRecording?.filename || selectedRecording?.original_filename || '녹음 파일';

      // 1. 상담 기록 생성 (source_recording_id로 녹음 파일 참조)
      const consultation = await consultationService.create({
        consultation_date: new Date().toISOString().split('T')[0],
        title: `상담 - ${filename}`,
        client_name: '',
        content: sttPreviewText || '',
        template_id: selectedTemplateId as number,
        source_recording_id: selectedRecordingId as number,  // 원본 녹음 파일 참조
      });

      // 2. 상담 기준으로 AI apply_prompt 호출 (LLM 분석 시작)
      try {
        await consultationService.applyPrompt(consultation.id, {
          prompt_id: selectedPromptId as number,
          role: llmRole,
        });
      } catch (llmErr) {
        console.error('Failed to start LLM processing:', llmErr);
        // LLM 시작 실패해도 계속 진행
      }

      // 3. 다이얼로그 닫고 목록 새로고침 (AI 분석 중 상태 확인 가능)
      setCreateDialogOpen(false);
      refetch();
    } catch (err) {
      console.error('Failed to create consultation:', err);
      setCreateError('상담 기록 생성에 실패했습니다.');
    } finally {
      setIsCreating(false);
    }
  };

  // 수동 상담 기록 생성 (선택된 녹음 파일과 템플릿을 함께 전달)
  const handleCreateManual = () => {
    setCreateDialogOpen(false);
    const params = new URLSearchParams();
    if (selectedRecordingId) {
      params.set('recordingId', selectedRecordingId.toString());
    }
    if (selectedTemplateId) {
      params.set('templateId', selectedTemplateId.toString());
    }
    const queryString = params.toString();
    navigate(`/consultations/new${queryString ? `?${queryString}` : ''}`);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDelete = (id: number) => {
    if (window.confirm('이 상담 기록을 삭제하시겠습니까?')) {
      deleteConsultation(id);
    }
  };

  // 통합 상태 칩: AI 상태 우선, 없으면 기본 상태 표시
  const getStatusChip = (status: string, llmStatus?: string, llmError?: string) => {
    // AI 분석 중
    if (llmStatus === 'processing') {
      return (
        <Chip
          icon={
            <AutoAwesomeIcon
              fontSize="small"
              sx={{
                animation: 'spin 2s linear infinite',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' },
                },
              }}
            />
          }
          label="AI 분석 중"
          color="info"
          size="small"
        />
      );
    }

    // AI 완료 또는 수동 완료
    if (llmStatus === 'completed' || status === 'completed') {
      return (
        <Chip
          icon={llmStatus === 'completed' ? <AutoAwesomeIcon fontSize="small" /> : undefined}
          label={llmStatus === 'completed' ? 'AI 완료' : '완료'}
          color="success"
          size="small"
        />
      );
    }

    // AI 실패
    if (llmStatus === 'failed') {
      return (
        <Tooltip title={llmError || 'AI 분석 실패'}>
          <Chip
            icon={<AutoAwesomeIcon fontSize="small" />}
            label="AI 실패"
            color="error"
            size="small"
          />
        </Tooltip>
      );
    }

    // 기본: 작성 중 (draft 상태, AI 상태 없음)
    return <Chip label="작성 중" color="default" size="small" />;
  };

  // 처리 중인 상담 수
  const processingCount = consultations.filter(c => c.llm_status === 'processing').length;

  return (
    <Box>
      <PageHeader
        title="상담 기록"
        subtitle="사용자별/날짜별 상담 기록을 관리합니다."
        action={
          <Button
            variant="contained"
            startIcon={<AutoAwesomeIcon />}
            onClick={handleOpenCreateDialog}
          >
            새 상담 기록
          </Button>
        }
      />

      <Card>
        <CardContent>
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <TextField
              placeholder="검색..."
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 300 }}
            />

            {/* 처리 중인 상담 표시 */}
            {processingCount > 0 && (
              <Chip
                icon={
                  <AutoAwesomeIcon
                    fontSize="small"
                    sx={{
                      animation: 'spin 2s linear infinite',
                      '@keyframes spin': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(360deg)' },
                      },
                    }}
                  />
                }
                label={`AI 분석 중 ${processingCount}건`}
                color="info"
                size="small"
                variant="outlined"
              />
            )}

            <Box sx={{ flexGrow: 1 }} />

            {/* 새로고침 버튼 */}
            <Tooltip title="목록 새로고침">
              <IconButton onClick={() => refetch()} size="small">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>

          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>상담일</TableCell>
                      <TableCell>제목</TableCell>
                      <TableCell>내담자</TableCell>
                      <TableCell>상태</TableCell>
                      <TableCell>생성일</TableCell>
                      <TableCell align="right">작업</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {consultations.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          상담 기록이 없습니다.
                        </TableCell>
                      </TableRow>
                    ) : (
                      consultations.map((consultation) => (
                        <TableRow key={consultation.id} hover>
                          <TableCell>
                            {format(new Date(consultation.consultation_date), 'yyyy-MM-dd', { locale: koLocale })}
                          </TableCell>
                          <TableCell>{consultation.title || '-'}</TableCell>
                          <TableCell>{consultation.client_name || '-'}</TableCell>
                          <TableCell>
                            {getStatusChip(consultation.status, consultation.llm_status, consultation.llm_error)}
                          </TableCell>
                          <TableCell>
                            {format(new Date(consultation.created_at), 'yyyy-MM-dd HH:mm', { locale: koLocale })}
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/consultations/${consultation.id}`)}
                              title="문서 작성"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(consultation.id)}
                              title="삭제"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[10, 20, 50]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="페이지당 행 수:"
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* AI 상담 생성 다이얼로그 */}
      <Dialog open={createDialogOpen} onClose={() => !isCreating && setCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoAwesomeIcon color="primary" />
          새 상담 기록 생성
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            녹음 파일을 선택하여 AI가 분석한 내용으로 상담일지를 자동 생성하거나, 직접 작성할 수 있습니다.
          </Typography>

          {/* WeDMS 연동 안내 */}
          {!hasWedmsToken && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              AI 분석 기능을 사용하려면 WeDMS 연동이 필요합니다. <strong>프로필 페이지</strong>에서 WeDMS 계정을 연동해주세요.
            </Alert>
          )}

          {loadingRecordings ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              {/* 녹음 파일 선택 */}
              <FormControl fullWidth size="small">
                <InputLabel>녹음 파일 선택</InputLabel>
                <Select
                  value={selectedRecordingId}
                  label="녹음 파일 선택"
                  onChange={(e) => setSelectedRecordingId(e.target.value as number)}
                  disabled={isCreating}
                >
                  {recordings.length === 0 ? (
                    <MenuItem disabled>STT 완료된 녹음 파일이 없습니다</MenuItem>
                  ) : (
                    recordings.map((recording) => (
                      <MenuItem key={recording.id} value={recording.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <MicIcon fontSize="small" color="action" />
                          {recording.original_filename || recording.filename}
                        </Box>
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>

              {/* 템플릿 선택 */}
              <FormControl fullWidth size="small">
                <InputLabel>문서 템플릿</InputLabel>
                <Select
                  value={selectedTemplateId}
                  label="문서 템플릿"
                  onChange={(e) => setSelectedTemplateId(e.target.value as number)}
                  disabled={isCreating}
                >
                  {templates.map((template) => (
                    <MenuItem key={template.id} value={template.id}>
                      {template.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* 프롬프트 선택 */}
              <FormControl fullWidth size="small">
                <InputLabel>AI 프롬프트</InputLabel>
                <Select
                  value={selectedPromptId}
                  label="AI 프롬프트"
                  onChange={(e) => setSelectedPromptId(e.target.value as number)}
                  disabled={isCreating}
                >
                  {prompts.map((prompt) => (
                    <MenuItem key={prompt.id} value={prompt.id}>
                      {prompt.name} {prompt.is_default && '(기본)'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* 시스템 역할 */}
              <TextField
                label="시스템 역할"
                value={llmRole}
                onChange={(e) => setLlmRole(e.target.value)}
                fullWidth
                size="small"
                disabled={isCreating}
                helperText="AI가 어떤 역할로 상담 내용을 분석할지 정의합니다"
              />

              {/* STT 텍스트 미리보기 */}
              {selectedRecordingId && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TextSnippetIcon fontSize="small" />
                      STT 변환 텍스트
                    </Typography>
                    {loadingSttPreview ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : (
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          maxHeight: 200,
                          overflow: 'auto',
                          bgcolor: 'grey.50',
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          {sttPreviewText || '(STT 텍스트가 없습니다)'}
                        </Typography>
                      </Paper>
                    )}
                  </Box>
                </>
              )}

              {/* 에러 메시지 */}
              {createError && (
                <Alert severity="error">
                  {createError}
                </Alert>
              )}

              {/* 녹음 파일 없음 안내 */}
              {recordings.length === 0 && !loadingRecordings && (
                <Alert severity="info">
                  STT 처리가 완료된 녹음 파일이 없습니다. 문서 관리에서 녹음 파일을 먼저 업로드해주세요.
                  또는 아래 &quot;직접 작성&quot; 버튼을 눌러 수동으로 상담 기록을 생성할 수 있습니다.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreateDialogOpen(false)} disabled={isCreating}>
            취소
          </Button>
          <Button
            variant="outlined"
            onClick={handleCreateManual}
            disabled={isCreating}
            startIcon={<AddIcon />}
          >
            직접 작성
          </Button>
          <Button
            onClick={handleCreateWithAI}
            variant="contained"
            disabled={!selectedRecordingId || !selectedTemplateId || !selectedPromptId || isCreating}
            startIcon={isCreating ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
          >
            {isCreating ? 'AI 분석 및 생성 중...' : 'AI로 생성'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConsultationListPage;
